import { useState } from "react";
import { Form, Button, Table, Row, Col } from "react-bootstrap";
import { AppLoader } from "../../../../Components/common/AppLoader/AppLoader";
import { MdDelete, MdEdit, MdWorkspacePremium } from "react-icons/md";
import { AwardCard } from "../../../../Components/ui/AwardCard/AwardCard";
import { mapAwardToCardProps, resolveAwardRecipientName, UPLOADS_BASE } from "../../../../api/awardsApi";

const EMPTY_FORM = {
  award_title: "",
  awarding_body: "",
  award_date: "",
  place: "",
  short_note: "",
};

const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString();
};

export const AwardsRecognitionsScreen = ({
  awards,
  loading,
  saving,
  onSave,
  onDelete,
}) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [imageFiles, setImageFiles] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [removedImageIds, setRemovedImageIds] = useState([]);
  const [existingImages, setExistingImages] = useState([]);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setImageFiles([]);
    setEditingId(null);
    setRemovedImageIds([]);
    setExistingImages([]);
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const startEdit = (award) => {
    setEditingId(award.award_id);
    setForm({
      award_title: award.award_title || "",
      awarding_body: award.awarding_body || "",
      award_date: award.award_date ? String(award.award_date).slice(0, 10) : "",
      place: award.place || "",
      short_note: award.short_note || "",
    });
    setImageFiles([]);
    setRemovedImageIds([]);
    setExistingImages(award.images || []);
  };

  const toggleRemoveExistingImage = (imageId) => {
    setRemovedImageIds((prev) =>
      prev.includes(String(imageId))
        ? prev.filter((id) => id !== String(imageId))
        : [...prev, String(imageId)]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const ok = await onSave(form, imageFiles, editingId, removedImageIds);
    if (ok) resetForm();
  };

  if (loading) {
    return <AppLoader message="Loading awards…" variant="portal" />;
  }

  return (
    <div className="awards-recognition-page">
      <div className="em-section-card">
        <h4 className="em-section-title">
          <MdWorkspacePremium className="media-gallery-title-icon" />
          Awards &amp; Recognition
        </h4>

        <Form onSubmit={handleSubmit}>
          <Row className="em-row">
            <Form.Group as={Col} md="6" controlId="awardTitle">
              <Form.Label>Award Title</Form.Label>
              <Form.Control
                type="text"
                value={form.award_title}
                onChange={(e) => handleChange("award_title", e.target.value)}
                placeholder="e.g. Best Hospital Award 2024"
                required
              />
            </Form.Group>
            <Form.Group as={Col} md="6" controlId="awardingBody">
              <Form.Label>Awarding Body</Form.Label>
              <Form.Control
                type="text"
                value={form.awarding_body}
                onChange={(e) => handleChange("awarding_body", e.target.value)}
                placeholder="e.g. Indian Medical Association"
              />
            </Form.Group>
          </Row>

          <Row className="em-row">
            <Form.Group as={Col} md="6" controlId="awardDate">
              <Form.Label>Date</Form.Label>
              <Form.Control
                type="date"
                value={form.award_date}
                onChange={(e) => handleChange("award_date", e.target.value)}
              />
            </Form.Group>
            <Form.Group as={Col} md="6" controlId="awardPlace">
              <Form.Label>Place</Form.Label>
              <Form.Control
                type="text"
                value={form.place}
                onChange={(e) => handleChange("place", e.target.value)}
                placeholder="Location"
              />
            </Form.Group>
          </Row>

          <Row className="em-row">
            <Form.Group as={Col} md="12" controlId="awardShortNote">
              <Form.Label>Short Note</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={form.short_note}
                onChange={(e) => handleChange("short_note", e.target.value)}
                placeholder="Brief description of the award"
              />
            </Form.Group>
          </Row>

          <Row className="em-row em-row--last">
            <Form.Group as={Col} md="12" controlId="awardImagesUpload">
              <Form.Label>Upload Images</Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                multiple
                className="em-file-input"
                key={editingId ? `edit-${editingId}` : "new-award-images"}
                onChange={(e) => setImageFiles(Array.from(e.target.files || []))}
              />
              <span className="em-field-hint">
                Select one or more images for this award or certificate
              </span>
            </Form.Group>
          </Row>

          {editingId && existingImages.length > 0 && (
            <div className="awards-existing-images">
              <p className="awards-existing-images__label">Existing images (click to remove)</p>
              <div className="em-gallery-preview">
                {existingImages.map((img) => {
                  const marked = removedImageIds.includes(String(img.image_id));
                  return (
                    <button
                      key={img.image_id}
                      type="button"
                      className={`em-gallery-thumb awards-existing-images__thumb${marked ? " awards-existing-images__thumb--removed" : ""}`}
                      onClick={() => toggleRemoveExistingImage(img.image_id)}
                    >
                      <img
                        src={`${UPLOADS_BASE}/uploads/${img.image_path}`}
                        alt="Award"
                      />
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {imageFiles.length > 0 && (
            <div className="em-gallery-preview media-gallery-selected-preview">
              {imageFiles.map((file) => (
                <div key={`${file.name}-${file.lastModified}`} className="em-gallery-thumb">
                  <img src={URL.createObjectURL(file)} alt={file.name} />
                </div>
              ))}
            </div>
          )}

          <div className="media-gallery-form__actions">
            {editingId && (
              <Button type="button" variant="outline-secondary" onClick={resetForm} disabled={saving}>
                Cancel Edit
              </Button>
            )}
            <Button type="submit" variant="primary" className="btn-submit" disabled={saving}>
              {saving ? "Saving…" : editingId ? "Update Award" : "Save Award"}
            </Button>
          </div>
        </Form>
      </div>

      {awards.length > 0 && (
        <div className="em-section-card">
          <h4 className="em-section-title">Saved Awards</h4>
          <Row className="g-4 awards-recognition-cards">
            {awards.map((award) => (
              <Col key={award.award_id} xs={12} md={6} lg={4}>
                <AwardCard {...mapAwardToCardProps(award)} />
              </Col>
            ))}
          </Row>
        </div>
      )}

      <div className="em-section-card media-gallery-table-card">
        <h4 className="em-section-title">Awards &amp; Recognition Records</h4>
        {awards.length === 0 ? (
          <p className="hm-card-text hm-card-text--muted">No awards added yet.</p>
        ) : (
          <div className="table-responsive">
            <Table striped bordered hover size="sm" className="media-gallery-table">
              <thead>
                <tr>
                  <th>Award ID</th>
                  <th>Award Title</th>
                  <th>Hospital / Doctor</th>
                  <th>Awarding Body</th>
                  <th>Date</th>
                  <th>Place</th>
                  <th>Short Note</th>
                  <th>Images</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {awards.map((award) => (
                  <tr key={award.award_id}>
                    <td>{award.award_id}</td>
                    <td>{award.award_title}</td>
                    <td>{resolveAwardRecipientName(award) || "—"}</td>
                    <td>{award.awarding_body || "—"}</td>
                    <td>{formatDate(award.award_date)}</td>
                    <td>{award.place || "—"}</td>
                    <td>{award.short_note || "—"}</td>
                    <td>{award.images?.length || 0}</td>
                    <td>
                      <div className="media-gallery-table__actions">
                        <button
                          type="button"
                          className="media-gallery-table__btn"
                          onClick={() => startEdit(award)}
                          aria-label="Edit"
                        >
                          <MdEdit />
                        </button>
                        <button
                          type="button"
                          className="media-gallery-table__btn media-gallery-table__btn--danger"
                          onClick={() => onDelete(award.award_id)}
                          aria-label="Delete"
                          disabled={saving}
                        >
                          <MdDelete />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AwardsRecognitionsScreen;
